import type { User, Group, GroupMember, ListItem } from '@prisma/client';

export type TUser = {
	id: User['id'];
	name: User['name'];
	email: User['email'];
	budget: User['budget'];
	pricingTier: User['pricingTier'];
	admin: User['admin'];
}

export type TListItem = {
	id: ListItem['id'];
	completed: ListItem['completed'];
	productLink: ListItem['productLink'];
	price: ListItem['price'];
	deadline: string;
	deadlineFormatted: string;
	item: ListItem['item'];
	slug: ListItem['slug'];
	notes: ListItem['notes'];
	authorId: ListItem['authorId'];
	groupId: Group['id'];
	groupSlug: Group['slug'];
	groupMemberId: GroupMember['id'];
	groupMember: {
		id: GroupMember['id'];
		name: GroupMember['name'];
		slug: GroupMember['slug'];
	};
	onlyForId: ListItem['onlyForId'];
	assignedToId: ListItem['assignedToId'];
	assignedTo: {
		id: User['id'];
		name: User['name'];
	};
	sharedWithId: ListItem['sharedWithId'];
	sharedWith: {
		id: User['id'];
		name: User['name'];
	};
	author: {
		id: User['id'];
		name: User['name'];
	};
	isOwner?: boolean;
	isSharedWithUser?: boolean;
	canEdit?: boolean;
	canDelete?: boolean;
	canUncheck?: boolean;
	recipientCanView: ListItem['recipientCanView'];
	everyoneCanView: ListItem['everyoneCanView'];
	canView: boolean;
	canAssign: boolean;
	canOnlyView: boolean;
	isAssigned: boolean;
	completedById: ListItem['completedById'];
	completedBy: {
		name: User['name'];
	};
};

export type TGroup = {
	id: Group['id'];
	name: Group['name'];
	slug: Group['slug'];
	description: Group['description'];
	colour: Group['colour'];
	budget: Group['budget'];
	layout: Group['layout'];
	authorId: Group['authorId'];
	groupType: Group['groupType'];
};

export type TGroupItem = {
	id: Group['id'];
	name: Group['name'];
	slug: Group['slug'];
	colour: Group['colour'];
	[key: string]: any;
	groupListItems: {
		id: ListItem['id'];
		slug: ListItem['slug'];
		completed: ListItem['completed'];
	}[];
	_count: {
		groupListItems: number;
		groupMembers: number;
	};
	author: {
		name: User['name'];
	}
};

export type TGroupMember = {
	id: string;
	name: string;
	slug: string;
	colour: string | null;
	authorId: string;
	user?: {
		id: string;
		name: string;
		passwordHash: string;
	};
	groupMemberListItems: TListItem[];
	sharedWith: TListItem[];
	completedCount: number;
	isOwner: boolean;
	budget?: number;
	spend?: number;
	userId?: string;
	totalCost?: number;
	hasCompleted?: number;
	canAssign: boolean;
};

export type TGroupSettings = {
	completed: boolean;
	budget: boolean;
	budgetType: string;
	gradient: boolean;
	layout: number;
};

export type TGroupAndSettings = TGroup & {
	settings: TGroupSettings
};

export type TColours = {
	[key: string]: {
		name: string;
		value: string;
	};
};

export type TEditor = GroupMember & {
	type: string;
};

export type TProductData = {
	name?: string;
	image?: string;
	description?: string;
	url?: string;
	sku?: string;
	brand?: {
		name?: string;
		image?: string;
		url?: string;
	};
	offers?: {
		image?: string;
		price?: string;
		lowPrice?: string;
		highPrice?: string;
		priceCurrency?: string;
		availability?: string;
		sku?: string;
		url?: string;
	}[];
	error?: string;
};

export type TListItemLoaderData = {
	groupMember: Omit<Group, 'createdAt' | 'updatedAt' | 'description' | 'colour'> & {
		user: {
			id: User['id'];
			name: User['name'];
			email: User['email'];
		},
		group: {
			id: Group['id'];
			slug: Group['slug'];
			groupType: EGroupType;
		}
	};
	groupMembers: {
		id: GroupMember['id'];
		name: GroupMember['name'];
		slug: GroupMember['slug'];
		colour: GroupMember['colour'];
		user: {
			id: User['id'];
		};
	}[];
	listItem: TListItem;
	userId: User['id'];
};

export type TListItemActionData = {
	formError?: string;
	fields?: {
		item?: string;
		deadline?: string;
		productLink?: string;
		price?: string;
		notes?: string;
	},
	fieldErrors?: {
		item?: string;
		deadline?: string;
		productLink?: string;
		price?: string;
		notes?: string;
	}
};

export type TRelease = {
	id: number;
	author: {
		login: string;
		avatar_url: string;
	};
	name: string;
	tag_name: string;
	body: string;
	html: string | Promise<string>;
	published: string;
	published_at: string;
};

export enum EPricingTier {
	Free = 0,
	Basic = 1,
	Full = 2,
}

export enum EGroupType {
	gift = 'gift',
	todo = 'todo',
	holiday = 'holiday'
}
